import { LANGUAGES, type Language } from "../types";
import type { Words } from "./copy";

/** Each language named in itself, because someone who wants to switch cannot
 *  necessarily read the language they are switching away from. The two
 *  Chinese ones each in their own script, and named apart: two entries both
 *  reading 中文 could not be told from one another. */
const NAMES: Record<Language, string> = {
  en: "English",
  pt: "Português",
  nl: "Nederlands",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  es: "Español",
  zh: "繁體中文",
  "zh-hans": "简体中文",
};

const ORDER: readonly Language[] = LANGUAGES;

const KEY = "minime.lang";

/** What they picked last time, if they ever picked. */
export function storedLanguage(): Language | null {
  try {
    const value = localStorage.getItem(KEY);
    return value && ORDER.includes(value as Language) ? (value as Language) : null;
  } catch {
    return null; // private browsing
  }
}

export function rememberLanguage(language: Language): void {
  try {
    localStorage.setItem(KEY, language);
  } catch {
    /* private browsing */
  }
}

/** The page is written in whichever language they filled the form in, which is
 *  a guess made from a form, not something they chose. A native select rather
 *  than a row of flags: a flag is a country and not a language, and on a phone
 *  this hands the job to the picker the phone already has. */
export default function LanguagePicker({
  language,
  words: t,
  onChange,
}: {
  language: Language;
  words: Words;
  onChange: (next: Language) => void;
}) {
  return (
    <label className="langpick">
      <span className="sr-only">{t.t("languageLabel")}</span>
      <select
        className="langpick-select"
        value={language}
        onChange={(e) => onChange(e.target.value as Language)}
      >
        {ORDER.map((code) => (
          <option key={code} value={code}>
            {NAMES[code]}
          </option>
        ))}
      </select>
      <span className="langpick-face" aria-hidden="true">
        {NAMES[language]}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </span>
    </label>
  );
}
